import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Puzzle.css';
import { puzzleAPI, dailyChallengeAPI } from './services/api';

// --- Date Helper Functions (DD-MM-YYYY format) ---
const formatDateToDDMMYYYY = (date) => {
  if (!date) return '';
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.error("formatDateToDDMMYYYY received invalid Date:", date);
    return 'Invalid Date';
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const parseDDMMYYYYtoDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return null;
  // Expecting DD-MM-YYYY
  const [day, month, year] = parts.map(Number);
  if (isNaN(day) || isNaN(month) || isNaN(year) || day < 1 || day > 31 || month < 1 || month > 12) {
    return null;
  }
  // Month is 0-indexed in JS Date constructor (0 for January)
  return new Date(year, month - 1, day);
};
// --- End Date Helper Functions ---

const NewPuzzle = () => {
    const navigate = useNavigate();
    const [grid, setPuzzleGrid] = useState([]);
    const [gridSize, setGridSize] = useState(4);
    const [puzzleName, setName] = useState('');
    const [description, setDescription] = useState('');
    const [difficulty, setDifficulty] = useState('');
    const [funFact, setFunFact] = useState('');
    const [serialNo, setSerialNo] = useState('');
    const [bookletNumber, setBookletNumber] = useState('');
    const [generateSerialNo, setGenerateSerialNo] = useState(true);
    const [isLoading, setLoading] = useState(false);
    const [newPuzzleId, setNewPuzzleId] = useState(null);
    const timeoutRef = useRef(null);

    // State for helper text and name validation
    const [latestAssignedDailyInfo, setLatestAssignedDailyInfo] = useState(null);
    const [isTomorrowCovered, setIsTomorrowCovered] = useState(false);
    const [allExistingPuzzleNames, setAllExistingPuzzleNames] = useState(new Set());
    const [isLoadingHelperData, setIsLoadingHelperData] = useState(false);
    const [isNameTaken, setIsNameTaken] = useState(false);
  
    const COLORS = ['red', 'blue', 'yellow', 'black'];
    const colorMapping = {
      red: 'r',
      blue: 'b',
      yellow: 'y',
      black: 'x',
    };
  
    useEffect(() => {
        initializeGrid(gridSize);
    }, [gridSize]);

    // Effect for fetching helper data based on gridSize
    useEffect(() => {
        const targetGridSizes = [4, 5, 6];
        if (targetGridSizes.includes(parseInt(gridSize, 10))) {
            const fetchHelperData = async () => {
                setIsLoadingHelperData(true);
                try {
                    const today = new Date();
                    const startDate = new Date();
                    startDate.setDate(today.getDate() - 30);
                    const endDate = new Date();
                    endDate.setDate(today.getDate() + 30);

                    const formattedStartDate = formatDateToDDMMYYYY(startDate);
                    const formattedEndDate = formatDateToDDMMYYYY(endDate);

                    const assignedChallengesResponse = await dailyChallengeAPI.getDailyChallengesRange(formattedStartDate, formattedEndDate);

                    let tempLatestDateObj = null;
                    let tempLatestInfo = null;
                    if (assignedChallengesResponse && typeof assignedChallengesResponse === 'object') {
                        Object.entries(assignedChallengesResponse).forEach(([dateStr, challengeData]) => {
                            // dateStr from API response keys is DD-MM-YYYY
                            const parsedDate = parseDDMMYYYYtoDate(dateStr); 
                            if (parsedDate) {
                                if (!tempLatestDateObj || parsedDate > tempLatestDateObj) {
                                    tempLatestDateObj = parsedDate;
                                    tempLatestInfo = { name: challengeData.name, date: dateStr };
                                }
                            }
                        });
                    }
                    setLatestAssignedDailyInfo(tempLatestInfo);

                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    // tomorrowStr needs to be DD-MM-YYYY to match API response keys
                    const tomorrowStr = formatDateToDDMMYYYY(tomorrow); 

                    if (assignedChallengesResponse && assignedChallengesResponse[tomorrowStr]) {
                        setIsTomorrowCovered(true);
                    } else {
                        setIsTomorrowCovered(false);
                    }

                    // Fetch all puzzle names for validation (only if not already fetched or gridSize changed significantly)
                    // This part is simplified as allExistingPuzzleNames will be populated by this effect.
                    const allPuzzlesResponse = await puzzleAPI.getAllPuzzles();
                    const namesSet = new Set();
                    if (Array.isArray(allPuzzlesResponse)) {
                        allPuzzlesResponse.forEach(puzzle => {
                            if (puzzle && puzzle.name) {
                                namesSet.add(puzzle.name.toLowerCase());
                            }
                        });
                    }
                    setAllExistingPuzzleNames(namesSet);
                    // Name validation will be handled by a separate useEffect

                } catch (error) {
                    console.error("Error fetching helper data:", error);
                    setLatestAssignedDailyInfo(null); // Reset on error
                    setIsTomorrowCovered(false); // Reset on error
                    setAllExistingPuzzleNames(new Set()); // Reset on error
                } finally {
                    setIsLoadingHelperData(false);
                }
            };
            fetchHelperData();
        } else {
            // Reset helper states if gridSize is not a target size
            setLatestAssignedDailyInfo(null);
            setIsTomorrowCovered(false);
            setAllExistingPuzzleNames(new Set());
            setIsNameTaken(false); // Reset name taken status if grid size is not relevant
            setIsLoadingHelperData(false);
        }
    }, [gridSize]); // Only re-run when gridSize changes

    // Effect for real-time name validation
    useEffect(() => {
        if (allExistingPuzzleNames.size > 0) {
            const trimmedNewNameLower = puzzleName.trim().toLowerCase();
            if (trimmedNewNameLower) {
                setIsNameTaken(allExistingPuzzleNames.has(trimmedNewNameLower));
            } else {
                setIsNameTaken(false); // Empty name is not "taken"
            }
        } else {
            // If names aren't loaded yet, or no names exist, assume not taken.
            // This might briefly show as not taken until allExistingPuzzleNames is populated.
            setIsNameTaken(false);
        }
    }, [puzzleName, allExistingPuzzleNames]); // Re-run only when puzzleName or the set of existing names changes

    // Effect for handling navigation when newPuzzleId changes
    useEffect(() => {
        if (newPuzzleId) {
            navigate(`/puzzle/${newPuzzleId}`);
            setNewPuzzleId(null); // Reset after navigation
        }
    }, [newPuzzleId, navigate]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const initializeGrid = (size) => {
        const newGrid = new Array(size).fill(null).map(() => 
            new Array(size).fill({ color: 'black', counter: 0 })
        );
        setPuzzleGrid(newGrid);
    };

    const convertToJSON = () => {
      const pattern = grid.map(row => 
        row.map(cell => colorMapping[cell.color])
      );
  
      return {
        Name: puzzleName,
        SerialNumber: serialNo,
        ...(parseInt(gridSize, 10) === 8 ? { BookletNumber: bookletNumber } : {}),
        Difficulty: difficulty,
        Description: description,
        FunFact: funFact,
        Pattern: pattern,
      };
    };
  
    const updateColorAndCounter = (row, col) => {
        setPuzzleGrid(prevGrid => prevGrid.map((rowArr, rowIndex) =>
            rowIndex === row
            ? rowArr.map((cell, colIndex) =>
                colIndex === col
                ? {
                    ...cell,
                    color: COLORS[(COLORS.indexOf(cell.color) + 1) % COLORS.length],
                    counter: cell.counter + 1
                }
                : cell
            )
            : rowArr
        ));
    };

    const handleGridSizeChange = (size) => {
        size = parseInt(size, 10);
        setGridSize(size);
        setGenerateSerialNo(size === 4); // This existing logic might need review if 5x5, 6x6 also auto-generate
        initializeGrid(size);
    };

    const handleNameChange = (e) => {
        const newName = e.target.value;
        setName(newName); // Update puzzleName state
      
        if (allExistingPuzzleNames.size > 0) {
          const trimmedNewNameLower = newName.trim().toLowerCase();
          if (trimmedNewNameLower) {
            setIsNameTaken(allExistingPuzzleNames.has(trimmedNewNameLower));
          } else {
            setIsNameTaken(false); // Empty name is not "taken"
          }
        } else {
            // If names aren't loaded yet, assume not taken or wait for load
            setIsNameTaken(false);
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        const puzzleData = convertToJSON();
        if (puzzleData.Name === '') {
            window.alert('Please enter a name for the puzzle.');
            setLoading(false);
            return;
        }
        // The server auto-generates an id for 4x4/5x5/6x6, but 8x8 must supply
        // one. Sending a blank serial matches an existing blank-serial puzzle
        // and comes back as the confusing "serial number already exists".
        if (parseInt(gridSize, 10) === 8 && puzzleData.SerialNumber.trim() === '') {
            window.alert('Please enter a serial number. 8x8 puzzles require one.');
            setLoading(false);
            return;
        }
        if (parseInt(gridSize, 10) === 8 && puzzleData.BookletNumber.trim() === '') {
            window.alert('Please enter a booklet number. 8x8 puzzles require one.');
            setLoading(false);
            return;
        }
        try {
            const data = await puzzleAPI.createOrUpdatePuzzle(puzzleData);
            const { difficulty, fice, num_solutions } = data;
            
            window.alert(`Puzzle submitted successfully!
                Number of solutions: ${num_solutions},
                Difficulty: ${difficulty}, FICE Score: ${fice}`);
            
            // Check if board_number is available
            if (data.board_number) {
                // If available, set it in state to trigger navigation
                setNewPuzzleId(data.board_number);
                setLoading(false); // Reset loading state
            } else {
                // If not immediately available, set a timeout to check again
                // This gives the server time to process and return the board_number
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                
                timeoutRef.current = setTimeout(() => {
                    // After timeout, navigate to home as fallback
                    console.log("Board number not available after timeout, navigating to home");
                    navigate('/');
                    setLoading(false); // Reset loading state
                }, 3000); // 3 second timeout
            }
        } catch (error) {
            console.error('Error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to submit puzzle. Please try again.';
            window.alert(errorMessage);
            setLoading(false);
        }
    };

    return (
        <div className="challenge-container">
            <select
                value={gridSize}
                onChange={(e) => handleGridSizeChange(e.target.value)}
                className="select-field"
            >
                <option value="4">4x4</option>
                <option value="5">5x5</option>
                <option value="6">6x6</option>
                <option value="8">8x8</option>
            </select>
            <input
                type="text"
                value={puzzleName}
                onChange={handleNameChange}
                placeholder="Name of Challenge"
                className="input-field"
                style={{ color: isNameTaken ? 'red' : 'inherit' }}
            />
            {([4, 5, 6].includes(parseInt(gridSize,10))) && (
              <div className="puzzle-name-helpers" style={{ marginTop: '8px', marginBottom: '8px', fontSize: '0.85em', paddingLeft: '5px' }}>
                {isLoadingHelperData ? (
                  <p>Loading helper info...</p>
                ) : (
                  <>
                    {latestAssignedDailyInfo && latestAssignedDailyInfo.name ? (
                      <p style={{ margin: '2px 0' }}>Latest assigned: {latestAssignedDailyInfo.name} (for {latestAssignedDailyInfo.date})</p>
                    ) : (
                      <p style={{ margin: '2px 0' }}>No daily challenges currently assigned or data unavailable.</p>
                    )}
                    {!isTomorrowCovered && (
                      <p style={{ margin: '2px 0', color: "red", fontWeight: "bold"}}>CRITICAL: No daily challenge is assigned for tomorrow!</p>
                    )}
                  </>
                )}
              </div>
            )}
            {!generateSerialNo && (
                <input
                    type="number"
                    value={serialNo}
                    onChange={(e) => setSerialNo(e.target.value)}
                    placeholder="Serial Number"
                    className="input-field"
                />
            )}
            {parseInt(gridSize, 10) === 8 && (
                <select
                    value={bookletNumber}
                    onChange={(e) => setBookletNumber(e.target.value)}
                    className="select-field"
                >
                    <option value="">Booklet Number</option>
                    <option value="1">Booklet 1</option>
                    <option value="2">Booklet 2</option>
                </select>
            )}
            <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description"
                className="textarea-field"
                maxLength={100}
            />
            <textarea
                value={funFact}
                onChange={(e) => setFunFact(e.target.value)}
                placeholder="Fun Fact"
                className="textarea-field"
                maxLength={100}
            />
            {!generateSerialNo && (
                <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="select-field"
                >
                    <option value="">Book Difficulty</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                    <option value="unrated">Unrated</option>
                </select>
            )}
            <div className="grid-container">
                <div className={`grid grid-${gridSize}x${gridSize}`} id="gridContainer">
                    {grid.map((row, rowIndex) => (
                        <React.Fragment key={rowIndex}>
                            {row.map((cell, colIndex) => (
                                <div
                                    key={colIndex}
                                    className="grid-cell"
                                    style={{ backgroundColor: cell.color }}
                                    onClick={() => updateColorAndCounter(rowIndex, colIndex)}
                                ></div>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>
            <button className="confirm-button" onClick={handleSubmit} disabled={isLoading}>
                {isLoading ? <div className="loader"></div> : 'Confirm'}
            </button>
        </div>
    );
};

export default NewPuzzle;
